// src/api/uploadFile.js
import { api } from "../client";

const unwrap = (res) => res?.data?.message ?? res?.data?.data ?? res?.data;

/**
 * Upload file to temporary location (no document required).
 * Used during form filling before application is created.
 * - onProgress: optional callback for upload progress (progressEvent) => void
 * Returns: { file_url, file_name, name, ... }
 */
export async function uploadFileTemporary({
  file,
  isPrivate = true,
  onProgress,
}) {
  if (!file) throw new Error("No file selected");

  const fd = new FormData();
  fd.append("file", file);
  fd.append("is_private", isPrivate ? "1" : "0");
  // ✅ Don't append doctype/docname - this uploads to temporary location

  const config = {
    headers: { "Content-Type": "multipart/form-data" },
  };

  // Add progress callback if provided
  if (onProgress) {
    config.onUploadProgress = (progressEvent) => {
      // Handle progress events - ensure we have valid data
      if (progressEvent && typeof progressEvent.loaded === 'number') {
        if (progressEvent.total && progressEvent.total > 0) {
          // Calculate percentage based on loaded/total
          // Cap at 99% until upload is actually complete
          const percentCompleted = Math.min(99, Math.round((progressEvent.loaded * 100) / progressEvent.total));
          onProgress(percentCompleted, progressEvent.loaded, progressEvent.total);
        } else if (progressEvent.lengthComputable === false && progressEvent.loaded > 0) {
          // If length is not computable, show conservative progress (not 100%)
          // Use a conservative estimate - don't go above 90% if we don't know total
          const estimatedPercent = Math.min(90, Math.round((progressEvent.loaded / (1024 * 1024)) * 10));
          onProgress(estimatedPercent, progressEvent.loaded, progressEvent.total);
        }
      }
    };
  }

  const res = await api.post("/api/method/upload_file", fd, config);

  // Usually returns: { file_url, file_name, name, ... }
  return unwrap(res);
}

/**
 * Attach an already-uploaded file to an Applications doc.
 * Used after application is created to link temporary files.
 * - fileUrl: The file_url from a previous temporary upload
 * - fieldname: optional but recommended if you have Attach fields
 */
export async function attachFileToApplication({
  fileUrl,
  applicationName,
  fieldname,
}) {
  if (!fileUrl) throw new Error("No file URL provided");
  if (!applicationName) throw new Error("Missing applicationName");

  const fd = new FormData();
  fd.append("file_url", fileUrl);
  fd.append("doctype", "Applications");
  fd.append("docname", applicationName);
  if (fieldname) fd.append("fieldname", fieldname);

  const res = await api.post("/api/method/upload_file", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  return unwrap(res);
}

/**
 * Upload + attach to Applications doc (legacy function, kept for backward compatibility).
 * - fieldname is optional but recommended if you have Attach fields
 * - onProgress: optional callback for upload progress (progressEvent) => void
 */
export async function uploadFileToApplication({
  file,
  applicationName,
  fieldname,
  isPrivate = true,
  onProgress,
}) {
  if (!file) throw new Error("No file selected");
  if (!applicationName) throw new Error("Missing applicationName");

  const fd = new FormData();
  fd.append("file", file);
  fd.append("is_private", isPrivate ? "1" : "0");
  fd.append("doctype", "Applications");
  fd.append("docname", applicationName);
  if (fieldname) fd.append("fieldname", fieldname);

  // api.post automatically handles multipart when data is FormData
  const config = {
    headers: { "Content-Type": "multipart/form-data" },
  };

  // Add progress callback if provided
  if (onProgress) {
    config.onUploadProgress = (progressEvent) => {
      // Handle progress events - ensure we have valid data
      if (progressEvent && typeof progressEvent.loaded === 'number') {
        if (progressEvent.total && progressEvent.total > 0) {
          // Calculate percentage based on loaded/total
          // Cap at 99% until upload is actually complete
          const percentCompleted = Math.min(99, Math.round((progressEvent.loaded * 100) / progressEvent.total));
          onProgress(percentCompleted, progressEvent.loaded, progressEvent.total);
        } else if (progressEvent.lengthComputable === false && progressEvent.loaded > 0) {
          // If length is not computable, show conservative progress (not 100%)
          // Use a conservative estimate - don't go above 90% if we don't know total
          const estimatedPercent = Math.min(90, Math.round((progressEvent.loaded / (1024 * 1024)) * 10));
          onProgress(estimatedPercent, progressEvent.loaded, progressEvent.total);
        }
      }
    };
  }

  const res = await api.post("/api/method/upload_file", fd, config);

  // Usually returns: { file_url, file_name, name, ... }
  return unwrap(res);
}
